/**
 * Shared chrome for `PageHeader` and `ObjectHeader` (TMMPDM-0034): the sticky frame (PAGE-006),
 * the action row (one contained primary, outlined secondaries, rare ones in an overflow menu —
 * PAGE-002 / PAGE-003 / ACT-001) and the breadcrumb trail (BREAD-001 / BREAD-003). Internal to
 * `components/page`.
 */
import { useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import {
  Box,
  Breadcrumbs,
  Button,
  IconButton,
  Link,
  Menu,
  MenuItem,
  Stack,
  Typography,
  useMediaQuery,
  useScrollTrigger,
  useTheme,
} from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { Link as RouterLink } from 'react-router-dom';
import { useCopy } from '@/hooks/useCopy';

export interface HeaderAction {
  label: string;
  onClick: () => void;
  disabled?: boolean;
}

export interface Crumb {
  label: string;
  /** A destination; the last crumb (the current page) usually has none. */
  to?: string;
  /** Link state. A hop back to the reseller keeps the list the record was opened from. */
  state?: unknown;
}

/**
 * Sticks under the fixed app bar (56px below `sm`, 64px above — MUI's Toolbar heights). Once the
 * page has scrolled it takes the paper background and a bottom divider; it is in normal flow, so
 * the tables below it are never covered at rest.
 *
 * WCAG 2.2 2.4.11 (TMMPDM-0070): a field that takes focus, or is scrolled into view, is never left
 * under it. While it is mounted the page's `scroll-padding-top` is the app bar plus this header's
 * own height, kept current as the title wraps or a line is added, and when the viewport crosses `sm`
 * (the app bar's height, so this header's `top`, changes without the header itself resizing).
 */
export function StickyHeader({ children }: { children: ReactNode }) {
  const scrolled = useScrollTrigger({ disableHysteresis: true, threshold: 0 });
  const theme = useTheme();
  const upSm = useMediaQuery(theme.breakpoints.up('sm'));
  const frame = useRef<HTMLDivElement>(null);
  useLayoutEffect(() => {
    const element = frame.current;
    if (!element) return;
    const root = document.documentElement;
    const apply = () => {
      // `top` is the app bar (56px / 64px by breakpoint); the header sits directly under it.
      const stuckAt = Number.parseFloat(window.getComputedStyle(element).top) || 0;
      root.style.scrollPaddingTop = `${Math.ceil(stuckAt + element.getBoundingClientRect().height)}px`;
    };
    apply();
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(apply);
    observer?.observe(element);
    return () => {
      observer?.disconnect();
      root.style.removeProperty('scroll-padding-top');
    };
    // `upSm` is a trigger, not an input: crossing `sm` re-runs the measure above.
  }, [upSm]);
  return (
    <Box
      ref={frame}
      data-sticky-header=""
      data-scrolled={scrolled ? 'true' : 'false'}
      sx={{
        position: 'sticky',
        top: { xs: 56, sm: 64 },
        zIndex: (theme) => theme.zIndex.appBar - 1,
        bgcolor: scrolled ? 'background.paper' : 'background.default',
        borderBottom: 1,
        borderColor: scrolled ? 'divider' : 'transparent',
        mx: -1,
        px: 1,
        pt: 1,
        pb: 2,
        mb: 2,
      }}
    >
      {children}
    </Box>
  );
}

export function HeaderCrumbs({ crumbs }: { crumbs: Crumb[] }) {
  const { t } = useCopy();
  return (
    <Breadcrumbs aria-label={t('components.breadcrumbs', 'Breadcrumb')} sx={{ mb: 0.5 }}>
      {crumbs.map((crumb) =>
        crumb.to ? (
          <Link
            key={crumb.label}
            component={RouterLink}
            to={crumb.to}
            {...(crumb.state !== undefined ? { state: crumb.state } : {})}
            underline="hover"
            color="inherit"
            variant="body2"
          >
            {crumb.label}
          </Link>
        ) : (
          <Typography key={crumb.label} variant="body2" color="text.primary" aria-current="page">
            {crumb.label}
          </Typography>
        ),
      )}
    </Breadcrumbs>
  );
}

interface HeaderActionsProps {
  primaryAction?: HeaderAction | undefined;
  secondaryActions?: HeaderAction[] | undefined;
  overflowActions?: HeaderAction[] | undefined;
}

export function HeaderActions({
  primaryAction,
  secondaryActions = [],
  overflowActions = [],
}: HeaderActionsProps) {
  const { t } = useCopy();
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  if (!primaryAction && secondaryActions.length === 0 && overflowActions.length === 0) return null;
  const moreLabel = t('components.moreActions', 'More actions');
  return (
    <Stack direction="row" spacing={1} sx={{ alignItems: 'center', flexWrap: 'wrap', rowGap: 1 }}>
      {secondaryActions.map((action) => (
        <Button
          key={action.label}
          variant="outlined"
          onClick={action.onClick}
          disabled={Boolean(action.disabled)}
        >
          {action.label}
        </Button>
      ))}
      {overflowActions.length > 0 ? (
        <>
          <IconButton
            aria-label={moreLabel}
            aria-haspopup="menu"
            aria-expanded={anchor ? 'true' : 'false'}
            onClick={(event) => setAnchor(event.currentTarget)}
          >
            <MoreVertIcon />
          </IconButton>
          <Menu anchorEl={anchor} open={Boolean(anchor)} onClose={() => setAnchor(null)}>
            {overflowActions.map((action) => (
              <MenuItem
                key={action.label}
                disabled={Boolean(action.disabled)}
                onClick={() => {
                  setAnchor(null);
                  action.onClick();
                }}
              >
                {action.label}
              </MenuItem>
            ))}
          </Menu>
        </>
      ) : null}
      {primaryAction ? (
        <Button
          variant="contained"
          onClick={primaryAction.onClick}
          disabled={Boolean(primaryAction.disabled)}
        >
          {primaryAction.label}
        </Button>
      ) : null}
    </Stack>
  );
}
